import BasicAlert from 'components/common/BasicAlert';
import FullViewLoading from 'components/common/FullViewLoading';
import Loading from 'components/common/Loading';
import { useUser } from 'hooks/useUser';
import { Suspense } from 'react';
import { Link, Outlet } from 'react-router';
import { isRecruiterUser } from 'types/user.types';
import { TEMPLATE_PERMISSIONS } from '@hubertai/screening-question-types';

export function NoMatch() {
  return (
    <div className="flex h-full max-h-full w-full">
      <div className="m-auto">
        <h2>No match! Page doesn&apos;t exist!</h2>
        <button className="">
          <Link to="/">Go to the home page</Link>
        </button>
      </div>
    </div>
  );
}

export function Forbidden403() {
  const user = useUser();
  return (
    <div className="flex h-screen">
      <div className="m-auto">
        <h2>403 Forbidden!</h2>
        <BasicAlert header={`Hello ${user?.first_name ?? ''}!`} text={'403 Forbidden!'} />
        <p>
          I am sorry. I know who you are - I believe who you say you are, but you just do not have permission to access
          this resource.
        </p>
        <p>Maybe if you ask the system administrator nicely, you&apos;ll get permission.</p>
        <button className="">
          <Link to="/">Go to the home page</Link>
        </button>
      </div>
    </div>
  );
}

export function Unauthorized401() {
  const user = useUser();
  return (
    <div className="flex h-screen">
      <div className="m-auto">
        <h2>401 Unauthorized!</h2>
        <BasicAlert header={`Hello ${user?.first_name ?? ''}!`} text={'403 Unauthorized!'} />
        <p>You are not authenticated - either not authenticated at all or authenticated incorrectly.</p>
        <p>
          Please{' '}
          <button className="color-blue">
            <Link to="/login">login</Link>
          </button>{' '}
          and try again.
        </p>
      </div>
    </div>
  );
}

export function ClientsOutlet() {
  return (
    <Suspense fallback={<FullViewLoading title="Loading..." />}>
      <Outlet />
    </Suspense>
  );
}

export function CandidatesOutlet() {
  return (
    <Suspense fallback={<Loading label="candidates view" />}>
      <Outlet />
    </Suspense>
  );
}

export function TemplatesOutlet() {
  return (
    <Suspense fallback={<Loading label="templates view" />}>
      <Outlet />
    </Suspense>
  );
}

export function InterviewsOutlet() {
  return (
    <Suspense fallback={<Loading label="Interviews view" />}>
      <Outlet />
    </Suspense>
  );
}

export function QuestionsOutlet() {
  return (
    <Suspense fallback={<Loading label="questions view" />}>
      <Outlet />
    </Suspense>
  );
}

export function AgentChatOutlet() {
  const user = useUser();
  if (isRecruiterUser(user)) {
    const hasPermissions = user.access_groups_permissions?.some(
      (x) =>
        x.permissions.includes(TEMPLATE_PERMISSIONS.ShareOrg) || x.permissions.includes(TEMPLATE_PERMISSIONS.Create),
    );

    if (!hasPermissions) return <Forbidden403 />;
  }

  return (
    <Suspense fallback={<Loading label="agent chat view" />}>
      <Outlet />
    </Suspense>
  );
}

export function ScoresOutlet() {
  return (
    <Suspense fallback={<Loading label="scores view" />}>
      <Outlet />
    </Suspense>
  );
}

export function JobsOutlet() {
  return (
    <Suspense fallback={<Loading label="jobs view" />}>
      <Outlet />
    </Suspense>
  );
}
